import type { FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";

const profileFormSchema = z.object({
	username: z
		.string()
		.min(2, { message: "ユーザー名は2文字以上で入力してください" })
		.max(30, { message: "ユーザー名は30文字以下で入力してください" }),
	displayName: z
		.string()
		.max(50, { message: "表示名は50文字以下で入力してください" })
		.optional(),
	bio: z
		.string()
		.max(160, { message: "自己紹介は160文字以下で入力してください" })
		.optional(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export const ProfileForm: FC = () => {
	const { user } = useAuth();
	const { toast } = useToast();

	const form = useForm<ProfileFormValues>({
		resolver: zodResolver(profileFormSchema),
		defaultValues: {
			username: user?.username || "",
			bio: user?.bio || "",
		},
	});

	const onSubmit = async (data: ProfileFormValues) => {
		try {
			// TODO: プロフィール更新のAPI呼び出し
			toast({
				title: "プロフィールを更新しました",
				description: "変更内容が保存されました",
			});
		} catch (error) {
			toast({
				title: "エラーが発生しました",
				description: "プロフィールの更新に失敗しました",
				variant: "destructive",
			});
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="username"
					render={({ field }) => (
						<FormItem>
							<FormLabel>ユーザー名</FormLabel>
							<FormControl>
								<Input placeholder="username" {...field} />
							</FormControl>
							<FormDescription>
								URLやメンションで使用される一意の識別子です
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="displayName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>表示名</FormLabel>
							<FormControl>
								<Input placeholder="表示名" {...field} />
							</FormControl>
							<FormDescription>アプリ内で表示される名前です</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="bio"
					render={({ field }) => (
						<FormItem>
							<FormLabel>自己紹介</FormLabel>
							<FormControl>
								<Textarea
									placeholder="自己紹介を入力してください"
									className="resize-none"
									{...field}
								/>
							</FormControl>
							<FormDescription>
								プロフィールページに表示される自己紹介文です
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button type="submit">変更を保存</Button>
			</form>
		</Form>
	);
};
